import { faFolderOpen } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { t as globalT, t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { ActionIcon, Group, Input, Stack, Table } from "@mantine/core";
import {
  ActionIconLink,
  ButtonLink,
  LoadingSuspense,
} from "@sweco-se/reality-frontend";
import { createFileRoute } from "@tanstack/react-router";
import { type FC, useMemo, useState } from "react";

import { api } from "@/api.ts";
import { useIsAdmin } from "@/hooks/useIsAdmin.ts";
import { useSuspenseQueryPostgrest } from "@/hooks/useSuspenseQueryPostgrest";

export const Route = createFileRoute("/customers/")({
  component: RouteComponent,
  head: () => ({
    meta: [
      {
        title: globalT`Sweco REALity` + " - " + globalT`Customers`,
      },
    ],
  }),
});

const Customers: FC<{ search: string }> = ({ search }) => {
  const { data } = useSuspenseQueryPostgrest(
    useMemo(() => {
      let q = api.from("customer").select("*,project(name)");
      if (search) {
        q = q.or(
          `organisation_number.ilike.%${search}%,name.ilike.%${search}%`,
        );
      }
      return q;
    }, [search]),
  );
  const { data: customerAssociatedProject } = useSuspenseQueryPostgrest(
    useMemo(
      () =>
        api
          .from("project")
          .select("customer_id")
          .in(
            "customer_id",
            data.map((c) => c.id),
          )
          .not("customer_domain", "is", null),
      [data],
    ),
  );

  return (
    <Table>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>
            <Trans>Name</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>Organisation Number</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>Is Geodatasamverkan Member</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>Can access REALity</Trans>
          </Table.Th>
          <Table.Th></Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {data.map((d) => (
          <Table.Tr key={d.id}>
            <Table.Td>{d.name}</Table.Td>
            <Table.Td>{d.organisation_number}</Table.Td>
            <Table.Td>
              {d.is_geodatasamverkan_member ? (
                <Trans>Yes</Trans>
              ) : (
                <Trans>No</Trans>
              )}
            </Table.Td>
            <Table.Td>
              {customerAssociatedProject.some((p) => p.customer_id === d.id) ? (
                <Trans>Yes</Trans>
              ) : (
                <Trans>No</Trans>
              )}
            </Table.Td>
            <Table.Td>
              <ActionIcon.Group>
                <ActionIconLink
                  to="/customers/$id"
                  params={{ id: String(d.id) }}
                >
                  <FontAwesomeIcon icon={faFolderOpen} />
                </ActionIconLink>
              </ActionIcon.Group>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
};

function RouteComponent() {
  const [search, setSearch] = useState("");

  const isAdmin = useIsAdmin();

  return (
    <Stack gap="lg">
      <Group>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t`Search customers...`}
        />
        {isAdmin ? (
          <ButtonLink to="/projects/create">
            <Trans>Create</Trans>
          </ButtonLink>
        ) : null}
      </Group>
      <LoadingSuspense>
        <Customers search={search} />
      </LoadingSuspense>
    </Stack>
  );
}
